import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { useFormikContext } from 'formik';
import { lapsedLicenseMonthsStates, isCustomerSpecificTask } from '@ourbranch/lookups';

import Section from 'core/components/section';
import { Card } from 'core/components/card';
import { FormField } from 'core/components/form';
import { getPreSaleChecklistConditions } from '../../helper';
import useStyles from './pre-sale-checklist.styles';
import { brandConstants } from 'brand-config';

// Inline version of createTaskUniqueKey from @ourbranch/onboarding-tasks-client
// (that package has backend-only dependencies that can't be imported in the browser)
const createTaskUniqueKey = (task) => {
  const { taskName, data } = task;
  if (isCustomerSpecificTask(taskName) && data?.customer) {
    return `${taskName}:${data.customer}`;
  }
  return taskName;
};

const buyingCondoPolicyText = `I attest that I've informed the member that an inspector may come to their condo within 2-3 weeks of the policy’s effective date as part of ${brandConstants.SHORT_NAME}’s normal due diligence.`;
const inColoradoText =
  "I attest that I've informed the member that we are occasionally unable to get all the necessary underwriting reports pre-sale, that it doesn't happen often and if it impacts their policy, we'll let them know right away.";
const confirmedApplicantsText = "I attest the insured's spouse is listed as the co-applicant.";
const enteredPriorInsuranceText =
  " I attest that I've confirmed the name of the insured's prior carrier(s) and entered their previous policy numbers for Stress Free Switch if possible.";
const confirmDiscountsText = "I attest that I've communicated applied discounts and the steps required to retain them.";
const confirmCoveragesText =
  "I attest that I've discussed all accepted and declined coverages with the named insured and have explained the risks of not carrying certain coverages.";
const proofOfPriorCoverageText =
  "I attest that I've received proof of prior insurance OR advised member they will need to provide it to retain their policy, OR received approval for ‘No Need No Prior’.";
const getLapsedDriversLicensesText = (state) =>
  `I attest that I have verified if all drivers listed on this policy have had a valid driver's license continuously for the last ${lapsedLicenseMonthsStates[state]} months, as well as their age first licensed.`;
const roofSurfacesEndorsementText =
  'I attest that I have explained to the applicant that covered roof claims will be paid according to a payment schedule, rather than replacement cost, because their Home policy does not have Roof Surfaces Extended (Repair/Replace) coverage.';
const confirmAllDriversLicenseText = 'I attest that all available driver’s license numbers have been provided.';
const getMichiganPIPText = (residents) =>
  `I have verified that the ${residents} residents listed as living in household is the total number of members in the household. I have verified that this number includes the member, their spouse, and any resident relatives that live with them.`;
const homeAgeInventoryDiscountText =
  "I attest that I've informed the member, due to the age of their home, they are required to complete the Inventory check in their Account within 15 days of bind or they risk their policy being cancelled.";
const communityDriveParticipationText = `I attest that I have explained to the applicant that they must download the ${brandConstants.SHORT_NAME} app and activate Community Drive within 15 days of purchase to retain the Community Drive discount.`;
const homeInspectionText = `I attest that I've informed the member that an inspector will come to their home within 2-3 weeks of the policy's effective date as part of ${brandConstants.SHORT_NAME}'s normal due diligence.`;

const getTrailingDocsText = ({
  hasSPP,
  hasInventoryDiscount,
  homeAgeInventoryDiscountInformed,
  hasConnectedHome,
  hasGoodStudent,
  hasTakenDrivingCourse,
  hasKiaOrHyundai
}) => {
  const proofMap = {
    hasSPP: 'scheduled personal property documentation',
    hasInventoryDiscount: 'completed inventory',
    hasConnectedHome: 'connected home proof',
    hasGoodStudent: 'good student proof',
    hasTakenDrivingCourse: 'driving course proof',
    hasKiaOrHyundai: 'Kia/Hyundai anti-theft update proof'
  };

  const proofList = Object.keys(proofMap)
    .filter((key) => {
      // The has* flags from the helper already account for uploaded documents:
      // - For customer-specific tasks (goodStudent, drivingCourse), the helper checks per-driver
      //   and only sets the flag to false when ALL drivers have uploaded
      // - For non-customer-specific tasks (SPP, connectedHome), the helper checks against tasksWithDocuments
      switch (key) {
        case 'hasSPP':
          return hasSPP;
        case 'hasInventoryDiscount':
          return hasInventoryDiscount && !homeAgeInventoryDiscountInformed;
        case 'hasConnectedHome':
          return hasConnectedHome;
        case 'hasGoodStudent':
          return hasGoodStudent;
        case 'hasTakenDrivingCourse':
          return hasTakenDrivingCourse;
        case 'hasKiaOrHyundai':
          return hasKiaOrHyundai;
        default:
          return false;
      }
    })
    .map((key) => proofMap[key]);

  if (proofList.length === 0) {
    return null;
  }

  const lastProof = proofList.pop();
  const proofString = proofList.join(', ') + (proofList.length ? ' and ' : '') + lastProof;

  return (
    <span>
      {`I attest that I've communicated that the following proof or documentation will be required within 15 days after
      the policy's effective date: `}
      <b>{proofString}. </b>
      {`The member will receive additional communications about the
      requirements for these items after the policy is bound. If the above is not provided, the related
      coverage/discount will be removed and may incur additional charges.`}
    </span>
  );
};

const excludedDriversText = (drivers) => {
  const excludedDriversArray = [];

  drivers.forEach((d) => d.excludeDriver && excludedDriversArray.push(`${d.firstName} ${d.lastName}`));

  return `I attest that I've communicated to the member that driver(s) ${new Intl.ListFormat('en').format(
    excludedDriversArray
  )} are excluded from the auto policy.`;
};

const getNoWindHailInformedText = () => ({
  AL: {
    firstPart:
      "I attest that I've informed the member that their home coverage does not cover wind & hail damage in their location and that this coverage must be purchased separately through",
    link: 'https://aiua.org/',
    linkText: 'AIUA.'
  },
  GA: {
    firstPart:
      "I attest that I've informed the member that their home coverage does not include wind & hail damage due to their home’s proximity to the coastline and that they can purchase this coverage through the",
    link: 'https://www.georgiaunderwriting.com/',
    linkText: 'GUA.'
  }
});

const getEarthquakeAndFloodInformedText = (includeEarthquake, includeFlood) => {
  if (!includeEarthquake && !includeFlood) {
    return null;
  }

  let secondPart = '';
  if (includeEarthquake) {
    secondPart = ' earthquake';
    if (includeFlood) {
      secondPart = `${secondPart} and flood coverages are`;
    } else {
      secondPart = `${secondPart} coverage is`;
    }
  } else if (includeFlood) {
    secondPart = ' flood coverage is';
  }

  return `I attest that I've informed the member that their${secondPart} billed and handled separately from their main home and/or auto policies.`;
};

const PreSaleChecklist = ({ offer, policyType, tasks = [] }) => {
  const classes = useStyles();
  const { quote } = offer;
  const { values } = useFormikContext();

  // Get tasks that already have documents uploaded
  const tasksWithDocuments = tasks.filter((task) => task.data?.hasDocument).map((task) => createTaskUniqueKey(task));

  const conditions = useMemo(() => {
    return getPreSaleChecklistConditions(quote, values, policyType, offer, tasksWithDocuments);
  }, [quote, values, policyType, offer, tasksWithDocuments]);

  const {
    communityDriveInformed,
    allApplicantsConfirmed,
    inColoradoInformed,
    priorInsuranceEntered,
    noWindHailInformed,
    earthquakeAndFloodInformed,
    proofOfPriorCoverageInformed,
    lapsedLicensesVerified,
    excludedDriversInformed,
    buyingCondoPolicyInformed,
    roofSurfacesEndorsementInformed,
    driversLicenseInformed,
    michiganPIPInformed,
    trailingDocsInformed,
    hasSPP,
    hasInventoryDiscount,
    hasConnectedHome,
    hasGoodStudent,
    hasTakenDrivingCourse,
    hasKiaOrHyundai,
    homeAgeInventoryDiscountInformed,
    homeInspectionInformed,
    hasFortifiedHome
  } = conditions;
  const noWindHailInformedText = getNoWindHailInformedText()[quote.correctedAddress.state];
  const earthquakeAndFloodInformedText = getEarthquakeAndFloodInformedText(quote.includeEarthquake, quote.includeFlood);
  const lapsedDriversLicensesText = getLapsedDriversLicensesText(quote.correctedAddress.state);

  // Generate trailing docs text, filtering out tasks with uploaded documents
  const trailingDocsText = getTrailingDocsText({
    hasSPP,
    hasInventoryDiscount,
    homeAgeInventoryDiscountInformed,
    hasConnectedHome,
    hasGoodStudent,
    hasTakenDrivingCourse,
    hasKiaOrHyundai,
    tasksWithDocuments
  });
  return (
    <Section title="Pre - Sale Checklist" type="SubSection">
      <Card className={classes.cardContainer} type="primaryEditable">
        <Grid container spacing={6} className={classes.checklistContainer}>
          {allApplicantsConfirmed && (
            <FormField
              className={classes.content}
              name="allApplicantsConfirmed"
              type="checkbox"
              label={confirmedApplicantsText}
              mode="light"
            />
          )}
          <FormField
            className={classes.content}
            name="confirmDiscounts"
            type="checkbox"
            label={confirmDiscountsText}
            mode="light"
          />
          {communityDriveInformed && (
            <FormField
              className={classes.content}
              name="communityDriveInformed"
              type="checkbox"
              label={communityDriveParticipationText}
              mode="light"
              topAlignCheckbox
            />
          )}
          <FormField
            className={classes.content}
            name="confirmCoverages"
            type="checkbox"
            label={confirmCoveragesText}
            mode="light"
            topAlignCheckbox
          />
          {buyingCondoPolicyInformed && (
            <FormField
              className={classes.content}
              name="buyingCondoPolicyInformed"
              type="checkbox"
              label={buyingCondoPolicyText}
              mode="light"
              topAlignCheckbox
            />
          )}
          {homeInspectionInformed && (
            <FormField
              className={classes.content}
              name="homeInspectionInformed"
              type="checkbox"
              label={homeInspectionText}
              mode="light"
              topAlignCheckbox
            />
          )}
          {roofSurfacesEndorsementInformed && (
            <FormField
              className={classes.content}
              name="roofSurfacesEndorsementInformed"
              type="checkbox"
              label={roofSurfacesEndorsementText}
              mode="light"
              topAlignCheckbox
            />
          )}
          {driversLicenseInformed && (
            <FormField
              className={classes.content}
              name="driversLicenseInformed"
              type="checkbox"
              label={confirmAllDriversLicenseText}
              mode="light"
            />
          )}
          {proofOfPriorCoverageInformed && (
            <FormField
              className={classes.content}
              name="proofOfPriorCoverageInformed"
              type="checkbox"
              label={proofOfPriorCoverageText}
              mode="light"
              topAlignCheckbox
            />
          )}
          {homeAgeInventoryDiscountInformed && (
            <FormField
              className={classes.content}
              name="homeAgeInventoryDiscountInformed"
              type="checkbox"
              label={homeAgeInventoryDiscountText}
              mode="light"
              topAlignCheckbox
            />
          )}
          {inColoradoInformed && (
            <FormField
              className={classes.content}
              name="inColoradoInformed"
              type="checkbox"
              label={inColoradoText}
              mode="light"
              topAlignCheckbox
            />
          )}
          {priorInsuranceEntered && (
            <FormField
              className={classes.content}
              name="priorInsuranceEntered"
              type="checkbox"
              label={enteredPriorInsuranceText}
              mode="light"
              topAlignCheckbox
            />
          )}
          {lapsedLicensesVerified && (
            <FormField
              className={classes.content}
              name="lapsedLicensesVerified"
              type="checkbox"
              label={lapsedDriversLicensesText}
              mode="light"
              topAlignCheckbox
            />
          )}
          {noWindHailInformed && noWindHailInformedText && (
            <FormField
              className={classes.content}
              name="noWindHailInformed"
              type="checkbox"
              label={
                <>
                  {noWindHailInformedText.firstPart}{' '}
                  <a
                    className={classes.link}
                    href={noWindHailInformedText.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {noWindHailInformedText.linkText}
                  </a>
                </>
              }
              mode="light"
              topAlignCheckbox
            />
          )}
          {earthquakeAndFloodInformed && earthquakeAndFloodInformedText && (
            <FormField
              className={classes.content}
              name="earthquakeAndFloodInformed"
              type="checkbox"
              label={earthquakeAndFloodInformedText}
              mode="light"
              topAlignCheckbox
            />
          )}
          {excludedDriversInformed && (
            <FormField
              className={classes.content}
              name="excludedDriversInformed"
              type="checkbox"
              label={excludedDriversText(quote?.drivers)}
              mode="light"
              topAlignCheckbox
            />
          )}
          <FormField
            className={classes.content}
            name="branchCommunicationConsent"
            type="checkbox"
            label={
              <>
                I have informed the customer that {brandConstants.SHORT_NAME}'s{' '}
                <a
                  className={classes.link}
                  href={brandConstants.PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  privacy policy
                </a>{' '}
                can be found at{' '}
                <a
                  className={classes.link}
                  href={`https://www.${brandConstants.DOMAIN}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {brandConstants.DOMAIN}
                </a>{' '}
                and advised the customer that by proceeding with the transaction they shall be bound by its{' '}
                <a
                  className={classes.link}
                  href={brandConstants.TERMS_OF_USE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  terms
                </a>{' '}
                and the customer confirmed that they understand and would like to proceed.
              </>
            }
            mode="light"
            topAlignCheckbox
          />
          {michiganPIPInformed && (
            <FormField
              className={classes.content}
              name="michiganPIPInformed"
              type="checkbox"
              label={getMichiganPIPText(quote.auto.pipAllResidents)}
              mode="light"
              topAlignCheckbox
            />
          )}
          {trailingDocsInformed && trailingDocsText && (
            <FormField
              className={classes.content}
              name="trailingDocsInformed"
              type="checkbox"
              label={trailingDocsText}
              mode="light"
              topAlignCheckbox
              fast={false}
            />
          )}
          {hasFortifiedHome && (
            <FormField
              className={classes.content}
              name="hasFortifiedHome"
              type="checkbox"
              label="I attest that I have communicated that the following proof or documentation will be required within 15 days after the policy’s effective date: Fortified Home certification. The member will receive additional communications about the requirements for these items after the policy is bound. If the above is not provided, the related coverage/discount will be removed and may incur additional charges."
              mode="light"
              topAlignCheckbox
            />
          )}
        </Grid>
      </Card>
    </Section>
  );
};

PreSaleChecklist.propTypes = {
  offer: PropTypes.object.isRequired,
  policyType: PropTypes.string.isRequired,
  tasks: PropTypes.array
};

export default PreSaleChecklist;
